import { OpenInNew } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Alert, Button, DialogContentText, Snackbar } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCancelClassroom } from '../api';
import { userHasOnlyStudentRole } from '../auth';
import ErrorDialog, { ErrorDialogState } from '../error-dialog/ErrorDialog';
import { apiPaths, authHeader } from '../queryClient';
import { useSignalREffect } from '../signalr/SignalRContext';
import SimulationLoadingDialog from '../simulation-loading-dialog/SimulationLoadingDialog';
import {
  ExerciseType,
  SessionErrorStatuses,
  SimulationCompletedNotification,
  SimulationEventMessage,
  SimulationRole,
  SimulationUpdatedNotification,
} from '../types';
import { getSimulationPath, isRunning, isScheduleExercise, simulationProgressLoaded } from '../utils/activeSimulation';
import { useActiveClassroom, useActiveSimulation } from './useActiveSimulation';

const LoadingSimulation = () => {
  const navigate = useNavigate();

  const { activeSimulation, setActiveSimulation } = useActiveSimulation();
  const { activeClassroom } = useActiveClassroom();

  const [showPopupToast, setShowPopupToast] = useState(false);
  const [simulationErrorDialog, setSimulationErrorDialog] = useState<ErrorDialogState>({
    open: false,
  });
  const [showMaintenanceToast, setShowMaintenanceToast] = useState(true);

  const isStudent = userHasOnlyStudentRole();
  const [loading, setLoading] = useState(false);
  const [exerciseId, setExerciseId] = useState<string>();

  useEffect(() => {
    if (activeClassroom) {
      setExerciseId(activeClassroom?.exercise.id);
    }
  }, [activeClassroom]);

  const onCancel = () => {
    setLoading(true);

    activeSimulation?.exercise.type === ExerciseType.Classroom && activeClassroom
      ? cancelClassroom()
      : cancelSimulation();
  };

  const { cancelClassroom } = useCancelClassroom(
    activeClassroom?.exercise.id,
    async () => cancelSimulation().then(async () => checkForActiveSimulation()),
    () => setLoading(false),
  );

  const cancelSimulation = async () =>
    await fetch(apiPaths.faulted(activeSimulation?.id), {
      headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        reason: 'Simulation canceled successfully.',
      }),
      method: 'POST',
    }).then(() => checkForActiveSimulation());

  // We need this check(recall active api) because the response from delete Classroom API
  // and Abandon API provide statuses Accepted(200) or Not found(404),
  // this doesn't mean that Simulation was completed(deleted).
  const checkForActiveSimulation = async () =>
    await fetch(`/api/${apiPaths.activeSimulations}`, {
      headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
      method: 'GET',
    }).then(({ status }) => (status === 200 || status === 404 ? checkForActiveSimulation() : cancelCompleted()));

  const cancelCompleted = () => {
    setLoading(false);
    setActiveSimulation(null);

    if (activeSimulation?.exercise.type === ExerciseType.Classroom) {
      navigate(`./teach/exercises/${exerciseId}`);
    }
  };

  useSignalREffect(
    SimulationEventMessage.SimulationUpdated,
    (...args: any[]) => {
      const notification = args[0] as SimulationUpdatedNotification;
      const simulationStartedAndRunning = isRunning(notification) && !isRunning(activeSimulation);

      if (simulationStartedAndRunning) {
        const opened = window.open(notification.url, '_blank');
        if (!opened) {
          setShowPopupToast(true);
        }
      }

      const exerciseId = notification?.exercise?.id;

      if (simulationStartedAndRunning && isScheduleExercise(exerciseId)) {
        navigate(
          getSimulationPath(isStudent, exerciseId, notification.id, notification.role, activeSimulation?.exercise.type),
        );
      }
    },
    [activeSimulation],
  );

  useSignalREffect(
    SimulationEventMessage.SimulationCompleted,
    (...args: any[]) => {
      const { message, status } = args[0] as SimulationCompletedNotification;
      setShowPopupToast(false);
      if (Object.values(SessionErrorStatuses).includes(status as SessionErrorStatuses) && !activeSimulation) {
        setSimulationErrorDialog({ message, open: true });
      }
    },
    [],
  );

  const maintenanceEndDate = import.meta.env.VITE_MAINTENANCE_END_DATE
    ? new Date(import.meta.env.VITE_MAINTENANCE_END_DATE)
    : null;
  const isMaintenanceMode =
    import.meta.env.VITE_MAINTENANCE_MODE.toLowerCase() === 'true' &&
    (!maintenanceEndDate || new Date() <= maintenanceEndDate);

  return (
    <>
      {activeSimulation && activeSimulation?.role !== SimulationRole.Student && (
        <SimulationLoadingDialog
          exerciseType={activeSimulation.exercise.type}
          message={activeSimulation.message}
          open={!isRunning(activeSimulation)}
          progress={activeSimulation.progress}
          simulatorType={activeSimulation.simulator.type}
        >
          {!simulationProgressLoaded(activeSimulation.progress) && activeSimulation.progress >= 20 && (
            <LoadingButton disabled={loading} loading={loading} onClick={onCancel} variant="outlined">
              Cancel
            </LoadingButton>
          )}
        </SimulationLoadingDialog>
      )}
      {activeSimulation?.url && (
        <Snackbar
          anchorOrigin={{ vertical: !isMaintenanceMode ? 'top' : 'bottom', horizontal: 'center' }}
          open={showPopupToast}
        >
          <Alert onClose={() => setShowPopupToast(false)} severity="warning">
            A popup blocker is active, so the simulation did not open. Please allow popups and open the simulation
            manually.
            <Button
              href={activeSimulation.url}
              rel="noreferrer"
              size="small"
              startIcon={<OpenInNew />}
              target="_blank"
              sx={{ py: 0, pl: 2 }}
            >
              Open
            </Button>
          </Alert>
        </Snackbar>
      )}

      {isMaintenanceMode && (
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ marginTop: 4 }}
          open={showMaintenanceToast}
        >
          <Alert severity="warning" onClose={() => setShowMaintenanceToast(false)}>
            <strong>Upcoming maintenance ({import.meta.env.VITE_MAINTENANCE_PERIOD})</strong>
            <br />
            {import.meta.env.VITE_MAINTENANCE_DESCRIPTION}
          </Alert>
        </Snackbar>
      )}

      <ErrorDialog
        title=""
        open={simulationErrorDialog.open}
        message={
          <>
            <DialogContentText color="text.primary" variant="body1" gutterBottom>
              {simulationErrorDialog.message ||
                "The simulator isn't working right now due to a small technical problem."}
            </DialogContentText>
            {!simulationErrorDialog.message && (
              <DialogContentText color="text.primary" variant="body1">
                Please try again soon!
              </DialogContentText>
            )}
          </>
        }
        transition={{
          onExited: () => setSimulationErrorDialog({ ...simulationErrorDialog, message: undefined }),
        }}
      >
        <Button variant="contained" onClick={() => setSimulationErrorDialog({ ...simulationErrorDialog, open: false })}>
          Dismiss
        </Button>
      </ErrorDialog>
    </>
  );
};

export default LoadingSimulation;
